import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { deleteBiller, deleteBillerFailure, deleteBillerSuccess, loadBillerBeneficiaries, loadBillerBeneficiariesFailure, loadBillerBeneficiariesSuccess, loadBillers, loadBillersFailure, loadBillersSuccess, loadPendingBills, loadPendingBillsFailure, loadPendingBillsSuccess, updateBiller, updateBillerFailure, updateBillerSuccess } from './actions';
import { SbkBillersDataService } from '../sbk-billers.service';
import { CustomNotificationService, setLoading } from '@sbk/shared';
import { Store } from '@ngrx/store';

@Injectable()
export class BillersEffects {
  private actions$ = inject(Actions);
  private store = inject(Store);

  /**
   * Load biller beneficiaries
   */
  loadBillerBeneficiaries$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBillerBeneficiaries),
      tap(() => this.store.dispatch(setLoading({ key: 'getBillerBeneficiaries', loading: true }))),
      switchMap(() =>
        this.dataService.getBillerBeneficiaries().pipe(
          map((response) => {
            const filter = response.filter(filterContacts('biller'));
            this.store.dispatch(setLoading({ key: 'getBillerBeneficiaries', loading: false }));
            return loadBillerBeneficiariesSuccess({ billerBeneficiaries: filter });
          }),
          catchError((error) => {
            this.store.dispatch(setLoading({ key: 'getBillerBeneficiaries', loading: false }));
            return of(loadBillerBeneficiariesFailure({ error }));
          })
        )
      )
    )
  );

  /**
   * Load billers
   */
  loadBillers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadBillers),
      switchMap(() =>
        this.dataService.getBillers().pipe(
          map((response) => loadBillersSuccess(
            {
              billers: response
              // billers: response.filter(filterContacts('biller'))
            }
          )),
          catchError((error) => of(loadBillersFailure({ error })))
        )
      )
    )
  );

  /**
   * Load pending bills
   */

  loadPendingBills$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(loadPendingBills),
      tap(() => this.store.dispatch(setLoading({ key: 'loadPendingBills', loading: true }))),
      mergeMap((action) => {
        return this.dataService.getPendingBills(action.payload)
          .pipe(
            map((response) => {
              this.store.dispatch(setLoading({ key: 'loadPendingBills', loading: false }));
              return loadPendingBillsSuccess({ pendingBills: response?.bills, responseMessage: response?.ResponseMessage });
            }),
            catchError((error) => {
              this.store.dispatch(setLoading({ key: 'loadPendingBills', loading: false }));
              return of(loadPendingBillsFailure({ error }));
            })
          );
      })
    );
  });

  /**
   * Update biller
   */

  updateBiller$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(updateBiller),
      mergeMap((action) => {
        return this.dataService.updateBiller(action.billerId, action.payload)
          .pipe(
            map(() => {
              const updatedBillerDetails = {
                ...action.payload,
              };
              return updateBillerSuccess({ updatedBillerDetails });
            }),
            catchError((error) => of(updateBillerFailure({ error })))
          );
      })
    );
  });

  updateBillerSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(updateBillerSuccess),
        tap(() => {
          this.customNotificationService.showNotification(
            'Biller updated!', `The biller has been updated successfully`, 'success');
        }),
      );
    },
    { dispatch: false }
  );

  updateBillerFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(updateBillerFailure),
        tap(() => {
          this.customNotificationService.showNotification(
            'Update failed!', 'Something went wrong while updating the biller', 'error');
        }),
      );
    },
    { dispatch: false }
  );

  /**
   * Delete biller
   */

  deleteBiller$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteBiller),
      mergeMap((action) => {
        return this.dataService.deleteBiller(action.billerId)
          .pipe(
            map(() => {
              return deleteBillerSuccess();
            }),
            catchError((error) => of(deleteBillerFailure({ error })))
          );
      })
    );
  });

  deleteBillerSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(deleteBillerSuccess),
        tap(() => {
          this.customNotificationService.showNotification(
            'Biller deleted!', 'The biller has been successfully deleted', 'success');
        }),
      );
    },
    { dispatch: false }
  );

  deleteBillerFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(deleteBillerFailure),
        tap(() => {
          this.customNotificationService.showNotification(
            'Deletion failed!', 'Something went wrong while deleting the biller', 'error');
        }),
      );
    },
    { dispatch: false }
  );

  constructor(
    private dataService: SbkBillersDataService,
    private customNotificationService: CustomNotificationService
  ) { }
}


function filterContacts(filterBy: string): any {
  return (contact: any) => {
    if (contact && contact['accounts'].length) {
      return contact['accounts'][0].additions.beneficiaryType === filterBy ? true : false;
    } else {
      return false;
    }
  };
}

